import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { PaginatedResponse } from '@app/core/models/shared/admin.model';
import {
  AdminPaymentItemModel,
  AdminPaymentDetailModel,
  PaymentMethodFilterModel,
} from '@app/core/models/admin/admin-payment.model';
import type { AdminPaymentsListParams } from '@app/core/models/admin/admin-payment.model';

const emptyResponse: PaginatedResponse<AdminPaymentItemModel> = {
  currentPageNumber: 0,
  pageSize: 0,
  totalElements: 0,
  totalPages: 0,
  isLastPage: true,
  isFirstPage: true,
  isEmpty: true,
  content: [],
};

@Injectable({
  providedIn: 'root',
})
export class AdminPaymentsService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);

  private get paymentsUrl(): string {
    return `${this.apiBaseUrl}/admin/payments`;
  }

  private get paymentMethodsFilterUrl(): string {
    return `${this.apiBaseUrl}/admin/payment-methods/filter`;
  }

  private toParams(params?: AdminPaymentsListParams): HttpParams {
    let p = new HttpParams();
    if (!params) return p;
    if (params.page !== undefined) p = p.set('page', params.page.toString());
    if (params.size !== undefined) p = p.set('size', params.size.toString());
    if (params.dateFrom) p = p.set('dateFrom', params.dateFrom);
    if (params.dateTo) p = p.set('dateTo', params.dateTo);
    if (params.iin) p = p.set('iin', params.iin);
    if (params.phone) p = p.set('phone', params.phone);
    if (params.lastName) p = p.set('lastName', params.lastName);
    if (params.certificateNumber) p = p.set('certificateNumber', params.certificateNumber);
    if (params.paymentMethodId) p = p.set('paymentMethodId', params.paymentMethodId);
    if (params.paymentMethodCode) p = p.set('paymentMethodCode', params.paymentMethodCode);
    if (params.currency) p = p.set('currency', params.currency);
    return p;
  }

  getPayments(
    params?: AdminPaymentsListParams
  ): Observable<PaginatedResponse<AdminPaymentItemModel>> {
    return this.http
      .get<PaginatedResponse<AdminPaymentItemModel>>(this.paymentsUrl, {
        params: this.toParams(params),
      })
      .pipe(
        catchError(err => {
          console.error('AdminPaymentsService.getPayments', err);
          return of(emptyResponse);
        })
      );
  }

  getPaymentById(id: string): Observable<AdminPaymentDetailModel | null> {
    return this.http.get<AdminPaymentDetailModel>(`${this.paymentsUrl}/${id}`).pipe(
      catchError(err => {
        console.error('AdminPaymentsService.getPaymentById', err);
        return of(null);
      })
    );
  }

  getPaymentMethodsFilter(): Observable<PaymentMethodFilterModel[]> {
    return this.http.get<PaymentMethodFilterModel[]>(this.paymentMethodsFilterUrl).pipe(
      catchError(err => {
        console.error('AdminPaymentsService.getPaymentMethodsFilter', err);
        return of([]);
      })
    );
  }
}
